import { createRendererFailure, createRendererFailureChannel } from "./renderer-contract.js";
import type {
  RendererDiagnostics,
  RendererFailure,
  RendererFailureInput,
  CanvasCommandTraceSnapshot,
  CanvasDrawCommand,
  CanvasPathOperation,
  SceneRenderer,
} from "./renderer-contract.js";
import { assertScenePlanV1 } from "./scene-plan.js";
import type {
  ScenePlan,
  ScenePlanSignature,
} from "./scene-plan.js";
import { slantDeltaX } from "./scene-geometry.js";
import {
  createSceneSemanticBuffer,
  resizeSceneSemanticBuffer,
  writeScenePlanToSemanticBuffer,
} from "./scene-semantics.js";
import type { SceneSemanticBuffer } from "./scene-semantics.js";

const MAX_DEVICE_PIXEL_RATIO = 2;
const FULL_CIRCLE_RADIANS = Math.PI * 2;
const MIN_VIEWPORT_DIMENSION = 1;
const MAX_VIEWPORT_DIMENSION = 16_384;
const MAX_BACKING_DIMENSION = 32_768;
const MAX_GEOMETRY_SCALAR = 65_536;

export interface CanvasRendererViewport {
  readonly width: number;
  readonly height: number;
  readonly devicePixelRatio: number;
}

export interface CanvasDevicePixelRatioQuery {
  readonly media: string;
  addEventListener(type: "change", listener: () => void): void;
  removeEventListener(type: "change", listener: () => void): void;
}

export interface CanvasResizeObserver {
  observe(root: HTMLElement): void;
  disconnect(): void;
}

/** Browser operations are isolated here so construction has one testable boundary. */
export interface CanvasRendererContextBoundary {
  createCanvas(root: HTMLElement): HTMLCanvasElement;
  getContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null;
  createTraceContext(root: HTMLElement): CanvasRenderingContext2D | null;
  createPath2D(): Path2D;
  createResizeObserver(listener: () => void): CanvasResizeObserver;
  createDevicePixelRatioQuery(root: HTMLElement): CanvasDevicePixelRatioQuery;
  measure(root: HTMLElement): CanvasRendererViewport;
}

const DEFAULT_CONTEXT_BOUNDARY: CanvasRendererContextBoundary = Object.freeze({
  createCanvas(root: HTMLElement): HTMLCanvasElement {
    return root.ownerDocument.createElement("canvas");
  },
  getContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
    return canvas.getContext("2d", { alpha: true });
  },
  createTraceContext(root: HTMLElement): CanvasRenderingContext2D | null {
    const traceCanvas = root.ownerDocument.createElement("canvas");
    return traceCanvas.getContext("2d", { alpha: true });
  },
  createPath2D(): Path2D {
    return new Path2D();
  },
  createResizeObserver(listener: () => void): CanvasResizeObserver {
    const observer = new ResizeObserver(listener);
    return {
      observe(root: HTMLElement): void { observer.observe(root); },
      disconnect(): void { observer.disconnect(); },
    };
  },
  createDevicePixelRatioQuery(root: HTMLElement): CanvasDevicePixelRatioQuery {
    const view = root.ownerDocument.defaultView;
    if (view === null) throw new Error("Canvas renderer requires a window for DPR observation");
    return view.matchMedia(`(resolution: ${view.devicePixelRatio}dppx)`);
  },
  measure(root: HTMLElement): CanvasRendererViewport {
    const bounds = root.getBoundingClientRect();
    return {
      width: bounds.width,
      height: bounds.height,
      devicePixelRatio: root.ownerDocument.defaultView?.devicePixelRatio ?? 1,
    };
  },
});

export class CanvasSceneRendererError extends Error {
  readonly failure: RendererFailure;

  constructor(
    input: RendererFailureInput,
    failure: RendererFailure = createRendererFailure(input),
  ) {
    super(failure.message);
    this.name = "CanvasSceneRendererError";
    this.failure = failure;
  }
}

interface StreakGeometry {
  readonly kind: "streak";
  readonly path: Path2D;
  readonly horizontalLength: number;
  readonly length: number;
}

interface RippleGeometry {
  readonly kind: "ripple";
  readonly path: Path2D;
  readonly radius: number;
}

interface AppliedScene {
  readonly plan: ScenePlan;
  lastDrawTimeMs: number;
  readonly signature: ScenePlanSignature;
  readonly width: number;
  readonly height: number;
  readonly devicePixelRatio: number;
  readonly inkColor: string;
  readonly accentColor: string;
  readonly streaks: readonly StreakGeometry[];
  readonly ripples: readonly RippleGeometry[];
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function readPaintStyle(style: string | CanvasGradient | CanvasPattern): string {
  if (typeof style === "string") return style;
  throw new TypeError("Canvas renderer observation requires string paint styles");
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nested of Object.values(value)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value;
}

function snapshotScenePlan(plan: ScenePlan): ScenePlan {
  return deepFreeze(structuredClone(plan));
}

function assertCompleteScenePlan(plan: ScenePlan): void {
  assertScenePlanV1(plan);
}

function normalizeViewport(viewport: CanvasRendererViewport): CanvasRendererViewport {
  if (!isFiniteNumber(viewport.width)
      || viewport.width < 0
      || viewport.width > MAX_VIEWPORT_DIMENSION
      || !isFiniteNumber(viewport.height)
      || viewport.height < 0
      || viewport.height > MAX_VIEWPORT_DIMENSION) {
    throw new RangeError("Canvas renderer viewport dimensions are outside render-safe bounds");
  }
  const width = viewport.width === 0 ? MIN_VIEWPORT_DIMENSION : viewport.width;
  const height = viewport.height === 0 ? MIN_VIEWPORT_DIMENSION : viewport.height;
  if (width < MIN_VIEWPORT_DIMENSION || height < MIN_VIEWPORT_DIMENSION) {
    throw new RangeError("Canvas renderer viewport dimensions are outside render-safe bounds");
  }
  const rawDevicePixelRatio = isFiniteNumber(viewport.devicePixelRatio)
    ? viewport.devicePixelRatio
    : 1;
  const devicePixelRatio = Math.min(
    MAX_DEVICE_PIXEL_RATIO,
    Math.max(1, rawDevicePixelRatio),
  );
  if (width * devicePixelRatio > MAX_BACKING_DIMENSION
      || height * devicePixelRatio > MAX_BACKING_DIMENSION) {
    throw new RangeError("Canvas renderer backing dimensions exceed render-safe bounds");
  }
  return {
    width,
    height,
    devicePixelRatio,
  };
}

function initializationError(
  code: "initialization" | "unavailable",
  message: string,
  cause?: unknown,
): CanvasSceneRendererError {
  return new CanvasSceneRendererError({
    kind: "canvas",
    phase: "initialize",
    code,
    message,
    recoverable: true,
    cause,
  });
}

/**
 * Precondition: the host enforces an exclusive adapter lifecycle and destroys
 * the previous adapter before constructing this per-instance style owner.
 */
export function createCanvasSceneRenderer(
  root: HTMLElement,
  boundary: CanvasRendererContextBoundary = DEFAULT_CONTEXT_BOUNDARY,
): SceneRenderer {
  let canvas: HTMLCanvasElement;
  try {
    canvas = boundary.createCanvas(root);
    canvas.setAttribute("data-testid", "experience-canvas");
  } catch (cause) {
    throw initializationError("initialization", "Canvas element creation failed", cause);
  }

  let acquiredContext: CanvasRenderingContext2D | null;
  try {
    acquiredContext = boundary.getContext(canvas);
  } catch (cause) {
    throw initializationError("initialization", "Canvas 2D context acquisition failed", cause);
  }
  if (acquiredContext === null) {
    throw initializationError("unavailable", "Canvas 2D context is unavailable");
  }
  const drawingContext = acquiredContext;
  const originalHostStyles = {
    position: root.style.position,
    isolation: root.style.isolation,
    overflow: root.style.overflow,
  };
  const constructionRollbackFailure = (cause: unknown, cleanupCause: unknown): CanvasSceneRendererError =>
    new CanvasSceneRendererError({
      kind: "canvas",
      phase: "initialize",
      code: "resource",
      message: "Canvas renderer construction rollback was incomplete",
      recoverable: false,
      cause: cleanupCause ?? cause,
    });

  try {
    canvas.setAttribute("aria-hidden", "true");
    canvas.setAttribute("role", "presentation");
    canvas.tabIndex = -1;
    canvas.style.display = "block";
    canvas.style.position = "absolute";
    canvas.style.inset = "0";
    canvas.style.minWidth = "100%";
    canvas.style.minHeight = "100%";
    canvas.style.zIndex = "-1";
    canvas.style.pointerEvents = "none";
    if (root.style.position === "" || root.style.position === "static") {
      root.style.position = "relative";
    }
    root.style.isolation = "isolate";
    root.style.overflow = "hidden";
    root.appendChild(canvas);
  } catch (cause) {
    let cleanupCause: unknown;
    const attempt = (operation: () => void): void => {
      try { operation(); } catch (failure) { cleanupCause ??= failure; }
    };
    attempt(() => { if (canvas.parentNode !== null) canvas.parentNode.removeChild(canvas); });
    attempt(() => { root.style.position = originalHostStyles.position; });
    attempt(() => { root.style.isolation = originalHostStyles.isolation; });
    attempt(() => { root.style.overflow = originalHostStyles.overflow; });
    if (cleanupCause !== undefined || canvas.parentNode !== null
        || root.style.position !== originalHostStyles.position
        || root.style.isolation !== originalHostStyles.isolation
        || root.style.overflow !== originalHostStyles.overflow) {
      throw constructionRollbackFailure(cause, cleanupCause);
    }
    throw initializationError("initialization", "Canvas renderer construction failed", cause);
  }

  const failures = createRendererFailureChannel();
  let acceptedPlan: ScenePlan | null = null;
  let applied: AppliedScene | null = null;
  let destroyRequested = false;
  let destroyed = false;
  let applyCount = 0;
  let frameCount = 0;
  let renderCount = 0;
  let observationBuildCount = 0;
  let observationDirty = true;
  let cachedDiagnostics: RendererDiagnostics | null = null;
  let renderState: CanvasCommandTraceSnapshot | null = null;
  let semanticBuffer: SceneSemanticBuffer | null = null;
  const semanticBounds = { width: 0, height: 0, pixelRatio: 0 };
  let releaseAttempts = 0;
  let releaseFailures = 0;
  let resizeObserver: CanvasResizeObserver | null = null;
  let resizeObserverAttached = false;
  let dprQuery: CanvasDevicePixelRatioQuery | null = null;
  let dprListenerAttached = false;
  let canvasRemovalPending = true;
  let backingWidthPending = true;
  let backingHeightPending = true;
  type CanvasStyleKey =
    | "display"
    | "position"
    | "inset"
    | "minWidth"
    | "minHeight"
    | "zIndex"
    | "pointerEvents"
    | "width"
    | "height";
  type HostStyleKey = "position" | "isolation" | "overflow";
  const pendingCanvasStyles = new Set<CanvasStyleKey>([
    "display",
    "position",
    "inset",
    "minWidth",
    "minHeight",
    "zIndex",
    "pointerEvents",
    "width",
    "height",
  ]);
  const pendingHostStyles = new Set<HostStyleKey>(["position", "isolation", "overflow"]);

  function markObservationDirty(): void {
    observationDirty = true;
    cachedDiagnostics = null;
  }

  function emitAndThrow(error: CanvasSceneRendererError): never {
    markObservationDirty();
    failures.emit(error.failure);
    throw error;
  }

  function reusableSemantics(plan: ScenePlan): SceneSemanticBuffer {
    if (semanticBuffer === null) {
      semanticBuffer = createSceneSemanticBuffer(plan);
    } else {
      resizeSceneSemanticBuffer(semanticBuffer, plan);
    }
    return semanticBuffer;
  }

  function prepareScene(
    nextScenePlan: ScenePlan,
    viewport: CanvasRendererViewport,
  ): AppliedScene {
    const backingWidth = Math.round(viewport.width * viewport.devicePixelRatio);
    const backingHeight = Math.round(viewport.height * viewport.devicePixelRatio);
    if (!Number.isSafeInteger(backingWidth) || !Number.isSafeInteger(backingHeight)
        || backingWidth < 1 || backingHeight < 1
        || backingWidth > MAX_BACKING_DIMENSION || backingHeight > MAX_BACKING_DIMENSION) {
      throw new RangeError("Canvas renderer backing dimensions are unsafe");
    }
    const streaks = new Array<StreakGeometry>(nextScenePlan.rain.primitives.length);
    for (let index = 0; index < nextScenePlan.rain.primitives.length; index += 1) {
      const primitive = nextScenePlan.rain.primitives[index];
      const horizontalLength = slantDeltaX(
        nextScenePlan.rain.slant,
        primitive.length,
        viewport.width,
        viewport.height,
      );
      const x = primitive.x * viewport.width;
      const phaseY = primitive.phase * viewport.height;
      if (!isFiniteNumber(horizontalLength) || Math.abs(horizontalLength) > MAX_GEOMETRY_SCALAR
          || !isFiniteNumber(x) || Math.abs(x) > MAX_GEOMETRY_SCALAR
          || !isFiniteNumber(phaseY) || Math.abs(phaseY) > MAX_GEOMETRY_SCALAR) {
        throw new RangeError("Canvas renderer streak geometry is unsafe");
      }
      const path = boundary.createPath2D();
      path.moveTo(0, 0);
      path.lineTo(horizontalLength, primitive.length);
      streaks[index] = {
        kind: "streak",
        path,
        horizontalLength,
        length: primitive.length,
      };
    }

    const ripples = new Array<RippleGeometry>(nextScenePlan.ripples.primitives.length);
    for (let index = 0; index < nextScenePlan.ripples.primitives.length; index += 1) {
      const primitive = nextScenePlan.ripples.primitives[index];
      const x = primitive.x * viewport.width;
      const y = primitive.y * viewport.height;
      if (!isFiniteNumber(x) || Math.abs(x) > MAX_GEOMETRY_SCALAR
          || !isFiniteNumber(y) || Math.abs(y) > MAX_GEOMETRY_SCALAR) {
        throw new RangeError("Canvas renderer ripple geometry is unsafe");
      }
      const path = boundary.createPath2D();
      path.arc(0, 0, primitive.radius, 0, FULL_CIRCLE_RADIANS);
      ripples[index] = {
        kind: "ripple",
        path,
        radius: primitive.radius,
      };
    }

    canvas.width = backingWidth;
    canvas.height = backingHeight;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    return {
      plan: nextScenePlan,
      lastDrawTimeMs: 0,
      signature: nextScenePlan.signature,
      width: viewport.width,
      height: viewport.height,
      devicePixelRatio: viewport.devicePixelRatio,
      inkColor: nextScenePlan.palette.ink,
      accentColor: nextScenePlan.palette.accent,
      streaks,
      ripples,
    };
  }

  function viewportIsUnchanged(viewport: CanvasRendererViewport): boolean {
    return applied !== null
      && applied.width === viewport.width
      && applied.height === viewport.height
      && applied.devicePixelRatio === viewport.devicePixelRatio;
  }

  function apply(nextScenePlan: ScenePlan): void {
    if (destroyRequested || destroyed) return;
    try {
      assertCompleteScenePlan(nextScenePlan);
    } catch (cause) {
      emitAndThrow(new CanvasSceneRendererError({
        kind: "canvas",
        phase: "apply",
        code: "apply",
        message: "Canvas renderer rejected an incomplete or invalid ScenePlan",
        recoverable: false,
        cause,
      }));
    }

    let viewport: CanvasRendererViewport;
    try {
      viewport = normalizeViewport(boundary.measure(root));
    } catch (cause) {
      emitAndThrow(new CanvasSceneRendererError({
        kind: "canvas",
        phase: "apply",
        code: "resource",
        message: "Canvas renderer viewport mapping failed",
        recoverable: true,
        cause,
      }));
    }

    if (applied !== null
        && applied.signature === nextScenePlan.signature
        && viewportIsUnchanged(viewport)) {
      return;
    }

    const previous = applied;
    let surfaceTouched = false;
    try {
      const acceptedSnapshot = snapshotScenePlan(nextScenePlan);
      reusableSemantics(acceptedSnapshot);
      surfaceTouched = true;
      const prepared = prepareScene(acceptedSnapshot, viewport);
      drawSceneUntraced(prepared, 0);
      applied = prepared;
      acceptedPlan = acceptedSnapshot;
      applyCount += 1;
      markObservationDirty();
    } catch (cause) {
      if (surfaceTouched && previous !== null) {
        try {
          canvas.width = Math.round(previous.width * previous.devicePixelRatio);
          canvas.height = Math.round(previous.height * previous.devicePixelRatio);
          canvas.style.width = `${previous.width}px`;
          canvas.style.height = `${previous.height}px`;
          reusableSemantics(previous.plan);
          drawSceneUntraced(previous, previous.lastDrawTimeMs);
          applied = previous;
        } catch (rollbackCause) {
          emitAndThrow(new CanvasSceneRendererError({
            kind: "canvas",
            phase: "apply",
            code: "resource",
            message: "Canvas renderer could not restore prior drawing resources",
            recoverable: false,
            cause: rollbackCause,
          }));
        }
      }
      emitAndThrow(new CanvasSceneRendererError({
        kind: "canvas",
        phase: "apply",
        code: "resource",
        message: "Canvas renderer geometry preparation failed",
        recoverable: true,
        cause,
      }));
    }
  }

  function handleViewportChange(): void {
    if (destroyRequested || destroyed || acceptedPlan === null) return;
    try {
      const viewport = normalizeViewport(boundary.measure(root));
      if (viewportIsUnchanged(viewport)) return;
      reusableSemantics(acceptedPlan);
      const prepared = prepareScene(acceptedPlan, viewport);
      drawSceneUntraced(prepared, 0);
      applied = prepared;
      markObservationDirty();
    } catch (cause) {
      markObservationDirty();
      failures.emit({
        kind: "canvas",
        phase: "apply",
        code: "resource",
        message: "Canvas renderer observed viewport rebuild failed",
        recoverable: true,
        cause,
      });
    }
  }

  function armDprObservation(): void {
    const nextQuery = boundary.createDevicePixelRatioQuery(root);
    dprQuery = nextQuery;
    dprListenerAttached = true;
    nextQuery.addEventListener("change", handleDprChange);
  }

  function disarmDprObservation(): void {
    const query = dprQuery;
    if (query === null || !dprListenerAttached) return;
    query.removeEventListener("change", handleDprChange);
    dprQuery = null;
    dprListenerAttached = false;
  }

  function handleDprChange(): void {
    try {
      markObservationDirty();
      disarmDprObservation();
      dprQuery = null;
      dprListenerAttached = false;
      handleViewportChange();
      if (!destroyRequested && !destroyed) armDprObservation();
    } catch (cause) {
      markObservationDirty();
      failures.emit({
        kind: "canvas",
        phase: "apply",
        code: "resource",
        message: "Canvas renderer DPR observation re-arm failed",
        recoverable: true,
        cause,
      });
    }
  }

  function drawSceneUntraced(
    scene: AppliedScene,
    nowMs: number,
  ): void {
    const dpr = scene.devicePixelRatio;
    const buffer = reusableSemantics(scene.plan);
    semanticBounds.width = scene.width;
    semanticBounds.height = scene.height;
    semanticBounds.pixelRatio = dpr;
    const semantics = writeScenePlanToSemanticBuffer(
      buffer,
      scene.plan,
      semanticBounds,
      nowMs,
    );
    drawingContext.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawingContext.globalAlpha = 1;
    drawingContext.clearRect(0, 0, scene.width, scene.height);
    drawingContext.fillStyle = semantics.paper.color;
    drawingContext.fillRect(0, 0, scene.width, scene.height);
    drawingContext.globalAlpha = semantics.wash.opacity;
    drawingContext.fillStyle = semantics.wash.color;
    drawingContext.fillRect(
      semantics.wash.rect.x,
      semantics.wash.rect.y,
      semantics.wash.rect.width,
      semantics.wash.rect.height,
    );
    if (semantics.paper.grainOpacity > 0) {
      drawingContext.globalAlpha = semantics.paper.grainOpacity;
      drawingContext.fillStyle = semantics.paper.grainColor;
      for (let y = 0; y < scene.height; y += semantics.paper.grainScalePx) {
        drawingContext.fillRect(0, y, scene.width, semantics.paper.pattern.rowHeightPx);
      }
    }
    drawingContext.strokeStyle = scene.inkColor;
    drawingContext.lineWidth = 1;
    for (let index = 0; index < scene.streaks.length; index += 1) {
      const streak = scene.streaks[index];
      const segment = semantics.rain[index];
      drawingContext.setTransform(dpr, 0, 0, dpr, segment.startX * dpr, segment.startY * dpr);
      drawingContext.globalAlpha = segment.alpha;
      drawingContext.stroke(streak.path);
    }
    drawingContext.strokeStyle = scene.accentColor;
    drawingContext.lineWidth = 1;
    for (let index = 0; index < scene.ripples.length; index += 1) {
      const ripple = scene.ripples[index];
      const rippleState = semantics.ripples[index];
      const scale = dpr * rippleState.radius / ripple.radius;
      drawingContext.setTransform(
        scale,
        0,
        0,
        scale,
        rippleState.centerX * dpr,
        rippleState.centerY * dpr,
      );
      drawingContext.globalAlpha = rippleState.alpha;
      drawingContext.stroke(ripple.path);
    }
    drawingContext.setTransform(dpr, 0, 0, dpr, 0, 0);
    drawingContext.globalAlpha = 1;
    renderCount += 1;
  }

  function drawSceneTraced(
    drawingContext: CanvasRenderingContext2D,
    scene: AppliedScene,
    nowMs: number,
  ): CanvasCommandTraceSnapshot {
      const commands: CanvasDrawCommand[] = [];
      const dpr = scene.devicePixelRatio;
      const buffer = reusableSemantics(scene.plan);
      semanticBounds.width = scene.width;
      semanticBounds.height = scene.height;
      semanticBounds.pixelRatio = dpr;
      const semantics = writeScenePlanToSemanticBuffer(
        buffer,
        scene.plan,
        semanticBounds,
        nowMs,
      );
      let transformA = dpr;
      let transformB = 0;
      let transformC = 0;
      let transformD = dpr;
      let translationX = 0;
      let translationY = 0;
      const setTransform = (
        a: number, b: number, c: number, d: number, e: number, f: number,
      ): void => {
        drawingContext.setTransform(a, b, c, d, e, f);
        transformA = a;
        transformB = b;
        transformC = c;
        transformD = d;
        translationX = e;
        translationY = f;
      };
      const traceTransform = (): readonly [number, number, number, number, number, number] => {
        const snapshot: [number, number, number, number, number, number] = [
          transformA, transformB, transformC, transformD, translationX, translationY,
        ];
        return Object.freeze(snapshot);
      };
      const clearRect = (x: number, y: number, width: number, height: number): void => {
        drawingContext.clearRect(x, y, width, height);
        const rect: [number, number, number, number] = [x, y, width, height];
        commands.push(Object.freeze({
          operation: "clearRect",
          rect: Object.freeze(rect),
          transform: traceTransform(),
        }));
      };
      const fillRect = (x: number, y: number, width: number, height: number): void => {
        drawingContext.fillRect(x, y, width, height);
        const rect: [number, number, number, number] = [x, y, width, height];
        commands.push(Object.freeze({
          operation: "fillRect",
          rect: Object.freeze(rect),
          transform: traceTransform(),
          fillStyle: readPaintStyle(drawingContext.fillStyle),
          alpha: drawingContext.globalAlpha,
        }));
      };
      const stroke = (geometry: StreakGeometry | RippleGeometry): void => {
        drawingContext.stroke(geometry.path);
        let path: readonly CanvasPathOperation[];
        if (geometry.kind === "streak") {
          path = Object.freeze([
            Object.freeze({ operation: "moveTo", x: 0, y: 0 }),
            Object.freeze({
              operation: "lineTo",
              x: geometry.horizontalLength,
              y: geometry.length,
            }),
          ]);
        } else {
          path = Object.freeze([
            Object.freeze({
              operation: "arc",
              x: 0,
              y: 0,
              radius: geometry.radius,
              startAngle: 0,
              endAngle: FULL_CIRCLE_RADIANS,
            }),
          ]);
        }
        commands.push(Object.freeze({
          operation: "stroke",
          path,
          transform: traceTransform(),
          strokeStyle: readPaintStyle(drawingContext.strokeStyle),
          lineWidth: drawingContext.lineWidth,
          alpha: drawingContext.globalAlpha,
        }));
      };
      setTransform(dpr, 0, 0, dpr, 0, 0);
      drawingContext.globalAlpha = 1;
      clearRect(0, 0, scene.width, scene.height);
      drawingContext.fillStyle = semantics.paper.color;
      fillRect(0, 0, scene.width, scene.height);
      drawingContext.globalAlpha = semantics.wash.opacity;
      drawingContext.fillStyle = semantics.wash.color;
      fillRect(
        semantics.wash.rect.x,
        semantics.wash.rect.y,
        semantics.wash.rect.width,
        semantics.wash.rect.height,
      );
      if (semantics.paper.grainOpacity > 0) {
        drawingContext.globalAlpha = semantics.paper.grainOpacity;
        drawingContext.fillStyle = semantics.paper.grainColor;
        for (let y = 0; y < scene.height; y += semantics.paper.grainScalePx) {
          fillRect(0, y, scene.width, semantics.paper.pattern.rowHeightPx);
        }
      }
      drawingContext.strokeStyle = scene.inkColor;
      drawingContext.lineWidth = 1;
      for (let index = 0; index < scene.streaks.length; index += 1) {
        const streak = scene.streaks[index];
        const segment = semantics.rain[index];
        setTransform(dpr, 0, 0, dpr, segment.startX * dpr, segment.startY * dpr);
        drawingContext.globalAlpha = segment.alpha;
        stroke(streak);
      }

      drawingContext.strokeStyle = scene.accentColor;
      drawingContext.lineWidth = 1;
      for (let index = 0; index < scene.ripples.length; index += 1) {
        const ripple = scene.ripples[index];
        const rippleState = semantics.ripples[index];
        const scale = dpr * rippleState.radius / ripple.radius;
        setTransform(
          scale, 0, 0, scale,
          rippleState.centerX * dpr,
          rippleState.centerY * dpr,
        );
        drawingContext.globalAlpha = rippleState.alpha;
        stroke(ripple);
      }
      setTransform(dpr, 0, 0, dpr, 0, 0);
      drawingContext.globalAlpha = 1;
      renderCount += 1;
      return Object.freeze({
        kind: "canvas-command-trace",
        commands: Object.freeze(commands),
      });
  }

  function frame(nowMs: number): void {
    if (destroyRequested || destroyed || applied === null) return;
    frameCount += 1;
    markObservationDirty();

    try {
      const contextWithLossProbe = drawingContext as CanvasRenderingContext2D & {
        isContextLost?: () => boolean;
      };
      if (contextWithLossProbe.isContextLost?.() === true) {
        emitAndThrow(new CanvasSceneRendererError({
          kind: "canvas",
          phase: "frame",
          code: "context-lost",
          message: "Canvas 2D context was lost",
          recoverable: true,
        }));
      }
    } catch (cause) {
      if (cause instanceof CanvasSceneRendererError) throw cause;
      emitAndThrow(new CanvasSceneRendererError({
        kind: "canvas",
        phase: "frame",
        code: "context-lost",
        message: "Canvas 2D context status failed",
        recoverable: true,
        cause,
      }));
    }

    if (!isFiniteNumber(nowMs)) {
      emitAndThrow(new CanvasSceneRendererError({
        kind: "canvas",
        phase: "frame",
        code: "frame",
        message: "Canvas renderer frame time must be finite",
        recoverable: true,
      }));
    }

    try {
      const scene = applied;
      drawSceneUntraced(scene, nowMs);
      scene.lastDrawTimeMs = nowMs;
    } catch (cause) {
      emitAndThrow(new CanvasSceneRendererError({
        kind: "canvas",
        phase: "frame",
        code: "frame",
        message: "Canvas 2D draw failed",
        recoverable: true,
        cause,
      }));
    }
  }

  function diagnostics(): RendererDiagnostics {
    if (!observationDirty && cachedDiagnostics !== null) return cachedDiagnostics;
    const canvasCount = canvas.parentNode === null ? 0 : 1;
    const objectCount = applied === null ? 0 : applied.streaks.length + applied.ripples.length;
    const activeSubscriptions = failures.activeSubscriptions()
      + (resizeObserver === null || !resizeObserverAttached ? 0 : 1)
      + (dprQuery === null || !dprListenerAttached ? 0 : 1);
    if (!destroyed && observationDirty) {
      if (applied !== null) {
        try {
          const traceContext = boundary.createTraceContext(root);
          if (traceContext === null || traceContext === drawingContext) {
            throw new Error("Canvas diagnostics requires an isolated trace context");
          }
          renderState = drawSceneTraced(traceContext, applied, applied.lastDrawTimeMs);
        } catch (cause) {
          const failureInput: RendererFailureInput = {
            kind: "canvas",
            phase: "frame",
            code: "resource",
            message: "Canvas diagnostics trace failed",
            recoverable: true,
            cause,
          };
          markObservationDirty();
          failures.emit(failureInput);
          const failure = failures.lastFailure();
          throw new CanvasSceneRendererError(failureInput, failure ?? createRendererFailure(failureInput));
        }
      } else {
        renderState = null;
      }
      observationBuildCount += 1;
    }
    observationDirty = false;
    cachedDiagnostics = Object.freeze({
      kind: "canvas",
      signature: applied?.signature ?? null,
      framePolicy: "host-raf",
      canvasCount,
      objectCount,
      activeSubscriptions,
      allocationsAfterApply: 0,
      lastFailure: failures.lastFailure(),
      observation: Object.freeze({
        kind: "canvas",
        applyCount,
          frameCount,
          renderCount,
          observationBuildCount,
          renderState: destroyed ? null : renderState,
        viewport: Object.freeze({
          width: applied?.width ?? 0,
          height: applied?.height ?? 0,
          pixelRatio: applied?.devicePixelRatio ?? 0,
        }),
        resources: Object.freeze({
          canvasCount,
          objectCount,
          activeSubscriptions,
          releaseAttempts,
          releaseFailures,
        }),
      }),
    });
    return cachedDiagnostics;
  }

  function onFailure(listener: (failure: RendererFailure) => void): () => void {
    const unsubscribe = failures.onFailure(listener);
    let subscribed = true;
    markObservationDirty();
    return (): void => {
      if (!subscribed) return;
      subscribed = false;
      unsubscribe();
      markObservationDirty();
    };
  }

  function destroy(): void {
    if (destroyed) return;
    destroyRequested = true;
    markObservationDirty();
    acceptedPlan = null;
    applied = null;

    let cleanupFailed = false;
    let firstCleanupCause: unknown;
    const attemptCleanup = (action: () => void, onSuccess: () => void): void => {
      releaseAttempts += 1;
      try {
        action();
        onSuccess();
      } catch (cause) {
        releaseFailures += 1;
        if (!cleanupFailed) firstCleanupCause = cause;
        cleanupFailed = true;
      }
    };

    if (resizeObserver !== null && resizeObserverAttached) {
      const observer = resizeObserver;
      attemptCleanup(() => observer.disconnect(), (): void => {
        resizeObserver = null;
        resizeObserverAttached = false;
      });
    }
    if (dprQuery !== null && dprListenerAttached) {
      const query = dprQuery;
      attemptCleanup((): void => {
        query.removeEventListener("change", handleDprChange);
      }, (): void => {
        dprQuery = null;
        dprListenerAttached = false;
      });
    }
    if (canvasRemovalPending) {
      attemptCleanup((): void => {
        if (canvas.parentNode !== null) canvas.parentNode.removeChild(canvas);
      }, (): void => {
        canvasRemovalPending = false;
      });
    }
    if (backingWidthPending) {
      attemptCleanup((): void => {
        canvas.width = 0;
      }, (): void => {
        backingWidthPending = false;
      });
    }
    if (backingHeightPending) {
      attemptCleanup((): void => {
        canvas.height = 0;
      }, (): void => {
        backingHeightPending = false;
      });
    }
    for (const property of [...pendingCanvasStyles]) {
      attemptCleanup((): void => {
        canvas.style[property] = "";
      }, (): void => {
        pendingCanvasStyles.delete(property);
      });
    }
    for (const property of [...pendingHostStyles]) {
      attemptCleanup((): void => {
        root.style[property] = originalHostStyles[property];
      }, (): void => {
        pendingHostStyles.delete(property);
      });
    }

    const cleanupComplete = resizeObserver === null && !resizeObserverAttached
      && dprQuery === null
      && !dprListenerAttached
      && !canvasRemovalPending
      && !backingWidthPending
      && !backingHeightPending
      && pendingCanvasStyles.size === 0
      && pendingHostStyles.size === 0;
    if (!cleanupComplete || cleanupFailed) {
      markObservationDirty();
      const error = new CanvasSceneRendererError({
        kind: "canvas",
        phase: "destroy",
        code: "cleanup",
        message: "Canvas renderer cleanup failed",
        recoverable: true,
        cause: firstCleanupCause,
      });
      failures.emit(error.failure);
      throw error;
    }

    destroyed = true;
    failures.destroy();
  }

  try {
    resizeObserver = boundary.createResizeObserver(handleViewportChange);
    resizeObserverAttached = true;
    resizeObserver.observe(root);
    armDprObservation();
  } catch (cause) {
    let cleanupCause: unknown;
    const attempt = (operation: () => void, onSuccess?: () => void): void => {
      try { operation(); onSuccess?.(); } catch (failure) { cleanupCause ??= failure; }
    };
    if (resizeObserver !== null && resizeObserverAttached) {
      attempt(() => resizeObserver?.disconnect(), () => {
        resizeObserver = null;
        resizeObserverAttached = false;
      });
    }
    if (dprQuery !== null && dprListenerAttached) attempt(disarmDprObservation);
    attempt(() => { if (canvas.parentNode !== null) canvas.parentNode.removeChild(canvas); });
    attempt(() => { canvas.width = 0; });
    attempt(() => { canvas.height = 0; });
    attempt(() => { root.style.position = originalHostStyles.position; });
    attempt(() => { root.style.isolation = originalHostStyles.isolation; });
    attempt(() => { root.style.overflow = originalHostStyles.overflow; });
    failures.destroy();
    if (cleanupCause !== undefined || resizeObserver !== null || resizeObserverAttached || dprListenerAttached
        || canvas.parentNode !== null || canvas.width !== 0 || canvas.height !== 0
        || root.style.position !== originalHostStyles.position
        || root.style.isolation !== originalHostStyles.isolation
        || root.style.overflow !== originalHostStyles.overflow) {
      throw constructionRollbackFailure(cause, cleanupCause);
    }
    throw initializationError("initialization", "Canvas viewport observation failed", cause);
  }

  return Object.freeze({
    kind: "canvas",
    apply,
    frame,
    onFailure,
    diagnostics,
    destroy,
  });
}
